import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { toWorkingHoursDayOfWeek } from '@/shared/lib/working-day';

const IST = 'Asia/Kolkata';

export interface DayVisitCandidate {
  status: string;
}

export interface DayHoursSchedule {
  dayOfWeek: number;
  isOpen: boolean;
  startTime: string;
  endTime: string;
}

export interface WorkingDayVisitStats {
  visits: number;
  waiting: number;
  inChair: number;
  done: number;
}

/**
 * Counts visits for the ops-day chip (active + checked-in + in chair + completed).
 */
export function summarizeDayVisits(appointments: DayVisitCandidate[]): WorkingDayVisitStats {
  let waiting = 0;
  let inChair = 0;
  let done = 0;
  let active = 0;

  for (const appt of appointments) {
    if (appt.status === 'cancelled' || appt.status === 'no_show') continue;
    if (appt.status === 'checked_in') {
      waiting += 1;
      active += 1;
      continue;
    }
    if (appt.status === 'in_progress') {
      inChair += 1;
      active += 1;
      continue;
    }
    if (appt.status === 'completed') {
      done += 1;
      continue;
    }
    active += 1;
  }

  return { visits: active + done, waiting, inChair, done };
}

/** Clinic schedule DOW (Mon=0) for an IST calendar day key. */
export function weekdayFromDateKey(dateKey: string): number {
  const jsDay = toZonedTime(parseISO(`${dateKey}T12:00:00`), IST).getDay();
  return toWorkingHoursDayOfWeek(jsDay);
}

/**
 * Formats clinic hours for the working-day chip secondary line.
 */
export function formatDayHoursLine(
  dateKey: string,
  schedules: DayHoursSchedule[] | undefined,
): string | null {
  if (!schedules?.length) return null;
  const dow = weekdayFromDateKey(dateKey);
  const day = schedules.find((s) => s.dayOfWeek === dow);
  if (!day) return null;
  if (!day.isOpen) return 'Closed';
  const end = day.endTime.slice(0, 5);
  return `Open · to ${end}`;
}

/**
 * Secondary subtitle for the working-day trigger.
 * Prefers visit stats when loaded; falls back to hours.
 */
export function workingDaySubtitle(
  stats: WorkingDayVisitStats | null,
  hoursLine: string | null,
  loading: boolean,
): string {
  if (loading && !stats) {
    return hoursLine ?? 'Loading day…';
  }
  if (stats) {
    const parts = [
      `${stats.visits} visit${stats.visits === 1 ? '' : 's'}`,
      stats.waiting > 0 ? `${stats.waiting} waiting` : null,
      stats.inChair > 0 ? `${stats.inChair} in chair` : null,
    ].filter(Boolean);
    const base = parts.join(' · ');
    if (hoursLine === 'Closed') return `${base} · Closed`;
    return base;
  }
  return hoursLine ?? 'No schedule data';
}

/** Full weekday + date for the popover title. */
export function workingDayPopoverTitle(dateKey: string): string {
  return format(toZonedTime(parseISO(`${dateKey}T12:00:00`), IST), 'EEEE, d MMMM');
}
